import { normalizeName } from './allergy-check.js';

/* ------------------------------------------------------------------ */
/*  Pure helpers for voice recordings & clinical extraction (PRD.271). */
/*  Kept free of PowerSync/React types so they stay unit-testable      */
/*  (see voice-recordings.test.ts), mirroring allergy-check.ts.        */
/* ------------------------------------------------------------------ */

/** Clear delimiter between concatenated recording segments before extraction. */
export const TRANSCRIPT_SEGMENT_DELIMITER = '\n\n─────\n\n';

interface TranscriptSegment {
  recorded_at: string | null;
  transcript: string | null;
}

/**
 * Concatenate the active recording segments chronologically (oldest first)
 * with clear delimiters. Empty/whitespace-only transcripts are skipped. This
 * is the exact string sent to the extraction endpoint (§ Transcript Behavior).
 */
export function assembleTranscript(segments: readonly TranscriptSegment[]): string {
  return [...segments]
    .sort((a, b) => (a.recorded_at ?? '').localeCompare(b.recorded_at ?? ''))
    .map((s) => (s.transcript ?? '').trim())
    .filter((t) => t.length > 0)
    .join(TRANSCRIPT_SEGMENT_DELIMITER);
}

/* ── Multiple-segment metadata + combined transcript (PRD.273) ── */

/** "Segment X of N" (1-based) when more than one segment exists, else null. */
export function segmentPositionLabel(index: number, total: number): string | null {
  if (total <= 1 || index < 0 || index >= total) return null;
  return `Segment ${index + 1} of ${total}`;
}

/** Visible delimiter between recordings in the combined transcript view. */
export const COMBINED_TRANSCRIPT_DELIMITER = '\n\n────────────────────────────────\n\n';

/**
 * Build a read-only combined transcript across active segments, chronological,
 * each preceded by a header. Empty segments are skipped. `formatHeader` lets the
 * UI inject a localized "Recording N — <timestamp>" header; the default is a
 * plain "Recording N".
 */
export function buildCombinedTranscript(
  segments: readonly TranscriptSegment[],
  formatHeader?: (recordedAt: string | null, index: number) => string,
): string {
  return [...segments]
    .sort((a, b) => (a.recorded_at ?? '').localeCompare(b.recorded_at ?? ''))
    .map((s) => ({ text: (s.transcript ?? '').trim(), recorded_at: s.recorded_at }))
    .filter((s) => s.text.length > 0)
    .map((s, i) => {
      const header = formatHeader ? formatHeader(s.recorded_at, i) : `Recording ${i + 1}`;
      return `${header}\n\n${s.text}`;
    })
    .join(COMBINED_TRANSCRIPT_DELIMITER);
}

/** Count active segments (non-empty transcript). */
export function activeSegmentCount(segments: readonly TranscriptSegment[]): number {
  return segments.filter((s) => (s.transcript ?? '').trim().length > 0).length;
}

/** Parse a client-side jsonb-stringified string[] column, defensively. */
export function parseStringArrayColumn(value: string | null | undefined): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export type AllergyMentionMatch = 'already_recorded' | 'possible_duplicate' | 'new';

/**
 * Compare an extracted allergy mention against the patient's existing
 * allergies. Exact normalized match → already recorded; word-boundary
 * containment either way → possible duplicate; otherwise new. Reuses the same
 * conservative normalization as prescription allergy checks.
 */
export function compareAllergyMention(
  mention: string,
  existingSubstances: readonly (string | null | undefined)[],
): AllergyMentionMatch {
  const needle = normalizeName(mention);
  if (!needle) return 'new';
  let possibleDuplicate = false;
  for (const raw of existingSubstances) {
    const hay = normalizeName(raw);
    if (!hay) continue;
    if (hay === needle) return 'already_recorded';
    if (` ${hay} `.includes(` ${needle} `) || ` ${needle} `.includes(` ${hay} `)) {
      possibleDuplicate = true;
    }
  }
  return possibleDuplicate ? 'possible_duplicate' : 'new';
}
